import {useMutation, useQueryClient} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'

import {projectSuggestionsQueryKey} from '../../queries/project-suggestions'
import {projectsQueryKey} from '../../queries/projects'

interface UseUpsertProjectLinksOptions {
  owner: string
  repo: string
}

export interface UpsertProjectLinksVariables {
  /** Project numbers to link to the repo (correspond to `projects_changes[<n>]=on`). */
  links: number[]
  /**
   * Project numbers to unlink from the repo. Submitted as `projects_changes[<n>]=`
   * (empty string) — Rails treats anything other than `"on"` as unlink, and the
   * existing Rails picker submits empty strings, so we mirror that exactly.
   */
  unlinks: number[]
}

/**
 * Mirrors the Rails `Repos::MemexesController#upsert_repo_project` form submission
 * for `action_type=link`, triggered from the Rails picker's Submit button. Used by
 * the React "Link a project" dialog to commit a batch of select/deselect decisions
 * in one request — `update_project_links` accepts both `"on"` and empty-string
 * entries in `projects_changes`, so a single call covers any mix of linking and
 * unlinking.
 *
 * On success, invalidates both the cached repo projects list (`projectsInfiniteQueryOptions`) and
 * the cached suggestions (`projectSuggestionsQueryOptions`) for the same `owner/repo` so
 * the list and the dialog's `selected:` flags both reflect the new server state.
 *
 * Does not optimistically prepend rows: a `ProjectSuggestion` lacks the rich fields
 * (`id`, `url`, `viewerCanWrite`, `closed`, …) the list row needs, so we let the
 * refetch fill them in. The brief loading shimmer is acceptable in exchange for
 * not fabricating partial rows that could clash with the next server payload.
 */
export function useUpsertProjectLinks({owner, repo}: UseUpsertProjectLinksOptions) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, UpsertProjectLinksVariables>({
    mutationFn: async ({links, unlinks}) => {
      // No-op submits (e.g. user opens dialog and hits Save without toggling
      // anything) resolve cleanly without firing a network request and without
      // invalidating caches. Mirrors the Rails picker, which also reaches the
      // controller's empty-`projects_changes` short-circuit and renders nothing.
      if (links.length === 0 && unlinks.length === 0) {
        return
      }

      // Mirror the Rails picker's form encoding: `action_type=link` plus a flat
      // `projects_changes[<number>]=on|off` per project being toggled. The Rails
      // controller reads this via `params[:projects_changes]`.
      const body = new URLSearchParams()
      body.append('action_type', 'link')
      for (const number of links) {
        body.append(`projects_changes[${number}]`, 'on')
      }
      for (const number of unlinks) {
        // Rails treats anything other than `"on"` (including `""`) as unlink. The
        // existing Rails picker submits empty strings; we match for consistency.
        body.append(`projects_changes[${number}]`, '')
      }

      const response = await reactFetch(
        `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/projects/beta/upsert`,
        {
          method: 'PUT',
          body,
          headers: {Accept: 'application/json'},
        },
      )

      if (response.ok) {
        return
      }

      // The companion Rails change responds with `{ error: <flash[:error] string> }`
      // for access/update failures; surface that text so the dialog can render it
      // inline. Falls back to a generic message for non-JSON 5xx responses.
      let message = `Failed to update project links: ${response.status}`
      try {
        const payload = (await response.json()) as {error?: string} | null
        if (payload?.error) {
          message = payload.error
        }
      } catch {
        // body wasn't JSON — keep the generic message.
      }
      throw new Error(message)
    },
    onSuccess: (_data, {links, unlinks}) => {
      // Skip cache invalidation when the mutation was a no-op so we don't churn
      // the list and dialog for nothing.
      if (links.length === 0 && unlinks.length === 0) {
        return
      }
      // Invalidate any cached projects page for this repo (the list view) and
      // any cached project-suggestions entry (the dialog's own data) so the
      // next read reflects the updated link set.
      void queryClient.invalidateQueries({queryKey: projectsQueryKey(owner, repo)})
      void queryClient.invalidateQueries({queryKey: projectSuggestionsQueryKey(owner, repo)})
    },
  })
}
